import { plans, segmentation } from '@thecompaniesapi/shared/client'
import { capitalize } from '@thecompaniesapi/string-helpers'
import * as changeCaseLib from 'change-case'
import cloneDeep from 'lodash/cloneDeep'
import numbroLib from 'numbro'
import { format, register } from 'timeago.js'
import { en_short as timeAgoEn, es as timeAgoEs, fr as timeAgoFr } from 'timeago.js/lib/lang'
import type { Action, ActionData, ActionStatus, CompanyDomain, PromptFeature } from '@thecompaniesapi/shared/client'
import { endpoints } from '~/config/endpoints.config'

interface ApiEndpoint {
  id: string
  isNew?: boolean
  loc: string
  method: string
  path: string
  text: string
  versions: string[]
}

register('en', timeAgoEn)
register('es', timeAgoEs)
register('fr', timeAgoFr)

export const changeCase = changeCaseLib

export function getColorForAttribute(attribute: 'monthlyVisitors', value: string) {
  if (attribute === 'monthlyVisitors') {
    if (value === 'under-10k') {
      return 'gray'
    }
    else if (value === '10k-50k') {
      return 'blue'
    }
    else if (value === '50k-100k') {
      return 'green'
    }
    else if (value === '100k-500k') {
      return 'indigo'
    }
    else if (value === '500k-1m') {
      return 'orange'
    }
    else if (value === '1m-10m') {
      return 'pink'
    }
    else if (value === '10m-50m') {
      return 'purple'
    }
    else if (value === '50m-100m') {
      return 'red'
    }
    else if (value === '100m-500m') {
      return 'teal'
    }
    else if (value === '500m-1b') {
      return 'white'
    }
    else if (value === 'over-1b') {
      return 'yellow'
    }
  }

  return null
}

export function getLogo(slug?: string, type?: 'companies' | 'technologies') {
  if (slug) {
    const { urls } = useAppConfig()

    if (type === 'companies') {
      return `${urls.storage}/companies/logos/square/${slug}.jpg`
    }
    else if (type === 'technologies') {
      return `${urls.storage}/technologies/logos/square/${slug}.jpg`
    }
  }
}

export function humanize(date: Date | number | string) {
  const cookie = useCookie('i18n_redirected')
  const locale = import.meta.client && cookie.value ? cookie.value : 'en'
  const timestamp = new Date(date).getTime()

  return format(timestamp, locale)
}

export function lockScroll(add = true) {
  if (add) {
    document.body.classList.add('overflow-hidden')
  }
  else {
    document.body.classList.remove('overflow-hidden')
  }
}

export function numbro(number: '∞' | null | number, format?: object | string) {
  if (number === null || number === undefined) {
    return ''
  }
  else if (number === '∞' || number % 1 !== 0) {
    return `${number}`
  }

  return numbroLib(number).format(format || '0,0')
}

export function toggleColorMode() {
  const colorMode = useColorMode()

  colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark'
}

export function truncate(text: string | undefined, maxLength: number) {
  if (!text || typeof text !== 'string') {
    return ''
  }

  if (text.length <= maxLength) {
    return text
  }

  return `${text.slice(0, maxLength)}…`
}

export function wait(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function downloadFile(input: ArrayBuffer | Blob | string, filename: string, mimeType?: string) {
  let url: string

  if (typeof input === 'string' && input.startsWith('http')) {
    // If input is already a URL, use it directly
    url = input
  }
  else {
    // Convert input to blob if it isn't already
    const blob = input instanceof Blob
      ? input
      : new Blob([input], { type: mimeType || 'application/octet-stream' })

    url = window.URL.createObjectURL(blob)
  }

  const link = document.createElement('a')

  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()

  // Only revoke if we created the URL
  if (!(typeof input === 'string' && input.startsWith('http'))) {
    window.URL.revokeObjectURL(url)
  }
}

export function getDomainsForAction(action: Action, completed?: boolean) {
  if (completed) {
    return Array.from(
      new Set([
        ...(action.data?.completed || []),
        ...(action.data?.completedDomainsSample || []),
      ]),
    )
  }

  return Array.from(
    new Set([
      ...(action.data?.domains || []),
      ...(action.data?.completedDomainsSample || []),
      ...(action.data?.failedDomainsSample || []),
      ...(action.data?.domainsSample || []),
      ...(action.data?.completed || []),
      ...(action.data?.failed || []),
    ]),
  )
}

export function getActionPingColor(status: ActionStatus) {
  switch (status) {
    case 'active':
      return 'teal'

    case 'completed':
      return 'green'

    case 'failed':
      return 'red'

    case 'pending':
      return 'yellow'

    default:
      return 'primary'
  }
}

export function getActionIcon(action: Action): string {
  switch ((action?.data as ActionData)?.job || action?.type) {
    case 'companies:added':
      return getIcon('add')

    case 'jobs:request':
      return getIcon('play')

    case 'cleanup-list':
      return getIcon('clean')

    case 'enrich-companies':
      return getIcon('refresh')

    case 'enrich-list':
      return getIcon('refresh')

    default:
      return getIcon('action')
  }
}

export function mapUrlToDocId(url: string): string | undefined {
  // Remove version prefix, api prefix, and normalize
  const normalizedUrl = url
    .replace(/^\/api\//, '/') // Keep leading slash, remove api prefix
    .replace(/^\/v[12]\//, '/') // Keep leading slash, remove version prefix
    .replace(/\/$/, '') // Remove trailing slash

  // Find matching endpoint
  return endpoints.find(
    (endpoint: ApiEndpoint) => {
      // Convert endpoint path to regex pattern
      const pattern = endpoint.path
        .replace(/:[^/]+/g, '[^/]+') // Replace :param with regex for any non-slash chars
        .replace(/\//g, '/') // No need to escape slashes in JS regex literals

      // Create regex with proper anchoring
      const regex = new RegExp(`^${pattern}$`)
      const matches = regex.test(normalizedUrl)

      return matches
    },
  )?.loc
}

export function isCompanyV2(company?: CompanyParent | CompanyV2): company is CompanyV2 {
  return typeof company?.domain === 'object'
}

export function isCompanyParent(company?: CompanyParent | CompanyV2): company is CompanyParent {
  return typeof company?.domain === 'string'
}

export function isCompanyRedirection(company?: CompanyParent | CompanyV2): boolean {
  return isCompanyV2(company) && !!company.domain?.redirection
}

export function getCompanyDescription(company?: CompanyParent | CompanyV2) {
  if (isCompanyV2(company)) {
    return (company as CompanyV2).descriptions?.tagline || ''
  }
  else if (isCompanyParent(company)) {
    return (company as CompanyParent).description || ''
  }
  else {
    return ''
  }
}

export function getCompanyName(company?: CompanyParent | CompanyV2) {
  let name = ''

  if (isCompanyV2(company)) {
    name = (company as CompanyV2).about?.name || ''
  }
  else if (isCompanyParent(company)) {
    name = (company as CompanyParent).name || ''
  }

  if (!name && company?.domain && typeof company?.domain === 'string') {
    name = company?.domain?.split('.')?.[0] || ''
  }

  if (!name && company?.domain && typeof company?.domain === 'object') {
    name = (company?.domain as CompanyDomain).domain?.split('.')?.[0] || ''
  }

  return name
}

export function getCompanyDomain(company?: CompanyParent | CompanyV2) {
  if (isCompanyV2(company)) {
    return (company as CompanyV2).domain?.domain || ''
  }
  else if (isCompanyParent(company)) {
    return (company as CompanyParent).domain || ''
  }
  else {
    return ''
  }
}

export function getSignsForAttribute(attribute: SegmentationAttribute, t: any) {
  const signs: USelectMenuOption[] = []

  if (['meta.syncedAt'].includes(attribute)) {
    signs.push({
      icon: getIcon('equal'),
      label: t('datapoints.signs.inTheLast'),
      value: 'equals',
    })
  }
  else if ([
    'about.industries',
    'meta.listIds',
    'socials',
    'technologies.active',
  ].includes(attribute)) {
    signs.push({
      icon: getIcon('equal'),
      label: t('datapoints.signs.includes'),
      value: 'equals',
    })
  }
  else if (['codes.naics', 'codes.sic'].includes(attribute)) {
    signs.push({
      icon: getIcon('startsWith'),
      label: t('datapoints.signs.startsWith'),
      value: 'equals',
    })
  }
  else {
    signs.push({
      icon: getIcon('equal'),
      label: t('datapoints.signs.equals'),
      value: 'equals',
    })
  }

  if (['meta.syncedAt'].includes(attribute)) {
    signs.push({
      icon: getIcon('notEqual'),
      label: t('datapoints.signs.notInTheLast'),
      value: 'notEquals',
    })
  }
  else if ([
    'about.industries',
    'meta.listIds',
    'socials',
    'technologies.active',
  ].includes(attribute)) {
    signs.push({
      icon: getIcon('notEqual'),
      label: t('datapoints.signs.notIncludes'),
      value: 'notEquals',
    })
  }
  else {
    signs.push({
      icon: getIcon('notEqual'),
      label: t('datapoints.signs.notEquals'),
      value: 'notEquals',
    })
  }

  if ([
    'about.industries',
    'codes.naics',
    'codes.sic',
  ].includes(attribute)) {
    signs.push({
      icon: getIcon('exactEqual'),
      label: t('datapoints.signs.exactEquals'),
      value: 'exactEquals',
    })
  }

  if (['meta.score'].includes(attribute)) {
    signs.push({
      icon: getIcon('greater'),
      label: t('datapoints.signs.greater'),
      value: 'greater',
    })

    signs.push({
      icon: getIcon('less'),
      label: t('datapoints.signs.lower'),
      value: 'lower',
    })
  }
  else if (['about.yearFounded', 'meta.syncedAt'].includes(attribute)) {
    signs.push({
      icon: getIcon('greater'),
      label: t('datapoints.signs.olderThan'),
      value: 'greater',
    })

    signs.push({
      icon: getIcon('less'),
      label: t('datapoints.signs.youngerThan'),
      value: 'lower',
    })
  }

  return signs
}

export function getOperators(t: any) {
  return [
    {
      icon: getIcon('and'),
      label: t('datapoints.operators.and'),
      value: 'and',
    },
    {
      icon: getIcon('or'),
      label: t('datapoints.operators.or'),
      value: 'or',
    },
  ]
}

export function getIconForAttribute(attribute: SegmentationAttribute) {
  const attributeIcons: Partial<{ [key in SegmentationAttribute]: UIcon }> = {
    'about.businessType': 'businessType',
    'about.industries': 'industries',
    'about.industry': 'mainIndustry',
    'about.totalEmployees': 'totalEmployees',
    'about.yearFounded': 'yearFounded',
    'ai.search': 'ai',
    'analytics.monthlyVisitors': 'monthlyVisitors',
    'apps': 'apps',
    'codes.naics': 'industryNaics',
    'codes.sic': 'industrySic',
    'contacts': 'contacts',
    'domain.tld': 'domainTld',
    'finances.revenue': 'revenue',
    'finances.stockExchange': 'stockExchanges',
    'locations.headquarters.city.code': 'city',
    'locations.headquarters.continent.code': 'continent',
    'locations.headquarters.country.code': 'country',
    'locations.headquarters.county.code': 'county',
    'locations.headquarters.state.code': 'state',
    'meta.listIds': 'list',
    'meta.score': 'score',
    'meta.syncedAt': 'refresh',
    'socials': 'socialNetworks',
    'socials.linkedin.id': 'linkedin',
    'technologies.active': 'technologies',
    'technologies.categories': 'technologyCategory',
  }

  return getIcon(attributeIcons[attribute] || 'default')
}

export function getBadgesForAttributes(params: {
  attribute: SegmentationAttribute
  suggestions: UInputMenuOption[]
  t: any
  values: (number | string)[]
}) {
  const t = params.t
  const suggestionsStore = useSuggestionsStore()
  const listsStore = useListsStore()

  return (params.values || []).map((value) => {
    let currentBadge: UBadge = {}

    if (['about.industries', 'about.industry'].includes(params.attribute)) {
      currentBadge = {
        ...currentBadge,
        label: capitalize(String(value).replaceAll('-', ' ')),
        value: String(value),
      }
    }
    else if ([
      'about.businessType',
      'about.totalEmployees',
      'finances.revenue',
    ].includes(params.attribute)) {
      currentBadge = {
        ...currentBadge,
        label: t(`ranges.${params.attribute.split('.')[1]}.${value}`),
        value: String(value),
      }
    }
    else if (['analytics.monthlyVisitors'].includes(params.attribute)) {
      currentBadge = {
        ...currentBadge,
        circle: getColorForAttribute('monthlyVisitors', String(value)),
        label: t(`ranges.${params.attribute.split('.')[1]}.${value}`),
        value: String(value),
      }
    }
    else if ([
      'locations.headquarters.city.code',
      'locations.headquarters.county.code',
      'locations.headquarters.state.code',
    ].includes(params.attribute)) {
      currentBadge = {
        ...currentBadge,
        flag: String(value).split(segmentation.separatorForLocationCode)[String(value).split(segmentation.separatorForLocationCode).length - 1],
        label: changeCase.noCase(String(value).split(segmentation.separatorForLocationCode)[0]),
        value: String(value),
      }
    }
    else if (['codes.naics'].includes(params.attribute)) {
      const suggestion = suggestionsStore.industriesNaics.find((suggestion) => {
        return suggestion.value === value
      })

      currentBadge = {
        ...currentBadge,
        label: String(value),
        tooltip: suggestion?.tooltip,
        value: String(value),
      }
    }
    else if (['codes.sic'].includes(params.attribute)) {
      const suggestion = suggestionsStore.industriesSic.find((suggestion) => {
        return suggestion.value === value
      })

      currentBadge = {
        ...currentBadge,
        label: String(value),
        tooltip: suggestion?.tooltip,
        value: String(value),
      }
    }
    else if (['locations.headquarters.country.code'].includes(params.attribute)) {
      currentBadge = {
        ...currentBadge,
        flag: String(value),
        label: t(`locations.countries.${value}`),
        value: String(value),
      }
    }
    else if (['locations.headquarters.continent.code'].includes(params.attribute)) {
      currentBadge = {
        ...currentBadge,
        icon: getIcon('continent'),
        label: t(`locations.continents.${value}`),
        value: String(value),
      }
    }
    else if (['meta.listIds'].includes(params.attribute)) {
      currentBadge = {
        ...currentBadge,
        label: truncate(listsStore.lists.find(list => list.id === Number.parseInt(String(value)))?.name || String(value), 25),
        value: String(value),
      }

      currentBadge.label = changeCase.noCase(currentBadge.label || '')
    }
    else if (['technologies.active'].includes(params.attribute)) {
      currentBadge = {
        ...currentBadge,
        label: String(value),
        value: String(value),
      }

      if (`${currentBadge.value}`.endsWith('-category')) {
        currentBadge.label = currentBadge.value?.replace('-category', '')
        currentBadge.icon = getIcon('technologyCategory')
      }
      else {
        currentBadge.avatar = getLogo(String(value).split('@')[0], 'technologies')

        if (currentBadge.value?.includes('@')) {
          currentBadge.label = currentBadge.value.split('@')[0]?.replace(/-js$/, '')

          currentBadge.menu = {
            id: String(value).split('@')[0],
            tooltip: String(value).split('@')[1] ? t('removeVersion') : t('addVersion'),
            value: String(value).split('@')[1] || '',
          }
        }
      }
    }
    else if (['apps'].includes(params.attribute)) {
      currentBadge = {
        ...currentBadge,
        icon: getIcon(String(value) as UIcon),
        label: String(value),
        value: String(value),
      }
    }
    else {
      currentBadge = cloneDeep(
        params.suggestions.find(suggestion => suggestion.value === value) || {
          ...currentBadge,
          label: capitalize(String(value)),
          value: String(value),
        },
      ) as UBadge
    }

    if (currentBadge) {
      currentBadge.color = 'indigo'

      if (!currentBadge.icon) {
        currentBadge.icon = getCurrentBadgeIcon(params.attribute, String(value))
      }
    }

    return currentBadge
  })
}

function getCurrentBadgeIcon(attribute: SegmentationAttribute, value: string) {
  const iconMap: { [key in SegmentationAttribute]?: string } = {
    'about.businessType': 'businessType',
    'about.industries': 'industries',
    'about.industry': 'mainIndustry',
    'about.totalEmployees': 'totalEmployees',
    'analytics.monthlyVisitors': 'monthlyVisitors',
    'apps': 'apps',
    'codes.naics': 'industryNaics',
    'codes.sic': 'industrySic',
    'domain.tld': 'domainTld',
    'finances.revenue': 'revenue',
    'finances.stockExchange': 'stockExchanges',
    'locations.headquarters.city.code': 'city',
    'locations.headquarters.continent.code': 'continent',
    'locations.headquarters.country.code': 'country',
    'locations.headquarters.county.code': 'county',
    'locations.headquarters.state.code': 'state',
    'meta.listIds': 'list',
  }

  if (attribute in iconMap) {
    return getIcon(iconMap[attribute] as UIcon)
  }
  else if (['socials'].includes(attribute)) {
    return getIcon(value as UIcon)
  }

  return getIcon(attribute as UIcon)
}

export function getAttributeDefaults() {
  const attributesDefaults: {
    [T in SegmentationAttribute]?: {
      operator?: SegmentationOperator
      sign?: SegmentationSign
      values?: (number | string)[]
    }
  } = {
    'about.businessType': {
      operator: 'or',
      sign: 'equals',
      values: ['public-company'],
    },
    'about.industries': {
      operator: 'or',
      sign: 'equals',
      values: [
        'software-development',
        'computer-software',
      ],
    },
    'about.industry': {
      operator: 'or',
      sign: 'equals',
      values: [
        'finance',
        'healthcare',
      ],
    },
    'about.totalEmployees': {
      operator: 'or',
      sign: 'equals',
      values: [
        '10-50',
        '50-200',
        '200-500',
        '500-1k',
      ],
    },
    'about.yearFounded': {
      operator: 'or',
      sign: 'lower',
      values: [2000],
    },
    'analytics.monthlyVisitors': {
      operator: 'or',
      sign: 'equals',
      values: [
        'under-10k',
        '10k-50k',
        '50k-100k',
      ],
    },
    'apps': {
      operator: 'or',
      sign: 'equals',
      values: [
        'android',
        'ios',
        'xbox',
      ],
    },
    'codes.naics': {
      operator: 'or',
      sign: 'equals',
      values: [
        '541511',
        '518210',
        '519130',
      ],
    },
    'codes.sic': {
      operator: 'or',
      sign: 'equals',
      values: [
        '7371',
        '7372',
        '7373',
      ],
    },
    'contacts': {
      operator: 'or',
      sign: 'equals',
      values: [
        'contactUrl',
        'emails',
        'phones',
      ],
    },
    'domain.tld': {
      operator: 'or',
      sign: 'equals',
      values: [
        'com',
        'org',
        'io',
        'co',
        'net',
      ],
    },
    'finances.revenue': {
      operator: 'or',
      sign: 'equals',
      values: [
        '1m-10m',
        '10m-50m',
        '50m-100m',
      ],
    },
    'finances.stockExchange': {
      operator: 'or',
      sign: 'equals',
      values: [
        'nasdaq',
        'nyse',
        'lse',
      ],
    },
    'locations.headquarters.city.code': {
      operator: 'or',
      sign: 'equals',
      values: [
        'san-francisco',
        'paris',
      ],
    },
    'locations.headquarters.continent.code': {
      operator: 'or',
      sign: 'equals',
      values: ['as'],
    },
    'locations.headquarters.country.code': {
      operator: 'or',
      sign: 'equals',
      values: [
        'us',
        'gb',
        'fr',
        'de',
        'ca',
        'jp',
      ],
    },
    'locations.headquarters.county.code': {
      operator: 'or',
      sign: 'equals',
      values: ['san-francisco|us', 'paris|fr'],
    },
    'locations.headquarters.state.code': {
      operator: 'or',
      sign: 'equals',
      values: [
        'california|us',
        'ile-de-france|fr',
      ],
    },
    'meta.score': {
      operator: 'or',
      sign: 'greater',
      values: [75],
    },
    'meta.syncedAt': {
      operator: 'or',
      sign: 'greater',
      values: ['last30days'],
    },
    'socials': {
      operator: 'or',
      sign: 'equals',
      values: [
        'linkedin',
        'twitter',
        'facebook',
        'github',
      ],
    },
    'technologies.active': {
      operator: 'or',
      sign: 'equals',
      values: [
        'nuxt-js',
        'next-js',
        'next-js-app-router',
        'astro',
      ],
    },
    'technologies.categories': {
      operator: 'or',
      sign: 'equals',
      values: [
        'javascript-framework',
        'analytics',
        'ecommerce',
      ],
    },
  } as const

  return attributesDefaults
}

export function getFeaturesIcons(): Record<SectionSearchFeature, string> {
  return {
    'ai-search': getIcon('ask'),
    'company-schema': getIcon('fileCode'),
    'emails': getIcon('email'),
    'search-by-name': getIcon('name'),
    'segmentation-attributes': getIcon('filter'),
    'segmentation-engine': getIcon('filter'),
    'similar-companies': getIcon('similar'),
    'social-networks-url': getIcon('socialNetworks'),
  } as const
}

export function smoothScrollTo(options?: {
  axis?: 'x' | 'y'
  container?: HTMLElement | Window
  duration?: number
  target: number
}) {
  const target = options?.target || 0
  const container = options?.container || window
  const axis = options?.axis || 'y'
  const duration = options?.duration || 100 // ms

  const isWindow = container === window

  const startX = isWindow
    ? window.scrollX || document.documentElement.scrollLeft
    : (container as HTMLElement).scrollLeft

  const startY = isWindow
    ? window.scrollY || document.documentElement.scrollTop
    : (container as HTMLElement).scrollTop

  const start = axis === 'x' ? startX : startY
  const distance = target - start

  let startTime: null | number = null

  function step(timestamp: number) {
    if (!startTime) {
      startTime = timestamp
    }
    const progress = timestamp - startTime
    const percentage = Math.min(progress / duration, 1)
    // Easing function for smoother animation
    const easing = (t: number) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1

    const value = start + distance * easing(percentage)

    if (isWindow) {
      window.scrollTo(
        axis === 'x' ? value : startX,
        axis === 'y' ? value : startY,
      )
    }
    else {
      if (axis === 'x') {
        (container as HTMLElement).scrollLeft = value
      }
      else {
        (container as HTMLElement).scrollTop = value
      }
    }

    if (progress < duration) {
      window.requestAnimationFrame(step)
    }
  }

  window.requestAnimationFrame(step)
}

export function getFeatureColor(feature: PromptFeature) {
  switch (feature) {
    case 'search':
      return 'blue'
    case 'similar':
      return 'indigo'
    case 'enrich':
      return 'green'
    case 'ask':
      return 'purple'
    case 'cleanup':
      return 'pink'
    default:
      return 'gray'
  }
}

export function creditsToCents(credits: number) {
  const ratio = plans.enterprise.pricing.monthly / plans.enterprise.credits

  return credits * ratio
}

export function formatNumber(value: number): string {
  if (value >= 1000000) {
    return numbroLib(value).format({
      average: true,
      mantissa: 1,
      optionalMantissa: true,
    })
  }

  return numbroLib(value).format({ thousandSeparated: true })
}
